<template>
  <template v-if="isMissingFilter">
    <DashboardMissingFilter />
  </template>

  <template v-else>
    <section class="kpi-section-header">
      <InfoLabel
        :title="t('dashboard__text__measurement_values')"
        :info="kpiInfo?.[locale] ?? t('dashboard__text__measurement_values-info')"
        :size="'mediumBold'"
        :fullscreen="sharedData?.fullscreen ?? false"
      />
    </section>
    <section class="kpi-section-content">
      <KPICard
        v-for="(props, i) in config"
        :key="i"
        v-bind="props"
        @toggleShowOnMap="() => (showOnMapIndex = showOnMapIndex === i ? undefined : i)"
      ></KPICard>
    </section>
    <ColorLegend
      v-if="showOnMapIndex !== undefined && !sharedData?.fullscreen && active"
      class="legend"
      :kpi="kpis[showOnMapIndex].kpi"
      :title="config[showOnMapIndex].title"
      v-bind="colorLegend?.props"
    />
  </template>
</template>
<script setup lang="ts">
import { computed, inject, onMounted, onUnmounted, ref, watch } from 'vue'
import ColorLegend from '@/design-system/atoms/ColorLegend.vue'
import KPICard, { Props as KPICardProps } from '../components/cards/KPICard.vue'
import InfoLabel from '@/design-system/molecules/InfoLabel.vue'
import DashboardMissingFilter from '../components/DashboardMissingFilter.vue'
import { useI18n } from 'vue-i18n'

import { sharedDataKey } from '../../types'
import { useStore } from 'vuex'
import useColoredEntities, { ColoredEntityData } from '@/composables/useColoredEntities'
import { Kpi, KpiQuery, KpiResult } from '@/api-client/models/buildingScore/Contracts'
import useFetchKpis from '@/api-client/models/buildingScore/useFetchKpis'
import { useFeature } from '@/pinia/features'
import {
  colorScale,
  getColor,
  getHeader,
  getIcon,
  getLabel,
  getRows,
  getTitle,
  getKpiInfo
} from './kpiConfig'
import listToObject from '@/utils/listToObject'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { FeatureType, useFilters } from '@/composables/useFilters'
import { storeToRefs } from 'pinia'

type Props = {
  title: LocaleString
  info?: LocaleString
}

defineProps<Props>()

const { active } = storeToRefs(useColoredEntities())

const sharedData = inject(sharedDataKey)
const { t, locale } = useI18n()
const filters = useFilters(FeatureType.BuildingScore)

const store = useStore()

const showOnMapEnabled = computed<boolean>(
  () =>
    store.state.clickedItemType === 'complex' ||
    store.state.clickedItemType === 'realEstate' ||
    store.state.clickedItemType === 'region'
)

const { feature: colorLegend } = useFeature(
  'home/analytics/portfolio/buildingscore/kpis/colorlegend'
)

const kpis: Kpi[] = [
  {
    kpi: 'BUILDING_SCORE',
    splitBy: 'CONSTRUCTION',
    parameters: {}
  },
  {
    kpi: 'TREND',
    splitBy: 'CONSTRUCTION',
    parameters: {
      type: 'ATTRACTIVENESS'
    }
  },
  {
    kpi: 'TREND',
    splitBy: 'CONSTRUCTION',
    parameters: {
      type: 'CONDITION'
    }
  }
]

const kpiQuery = computed<KpiQuery | undefined>(() => {
  const complexId = filters.submitted?.complex?.belongsToId
  const year = filters.submitted?.year
  if (!year) return undefined
  const constructions = filters.submitted?.buildings?.map((b) => b.id) ?? []
  const query: KpiQuery = {
    filter: {
      complexes: complexId ? [complexId] : undefined,
      constructions: constructions.length > 0 ? constructions : undefined
    },
    kpis: kpis.map((x) => {
      switch (x.kpi) {
        case 'BUILDING_SCORE':
          return {
            ...x,
            parameters: {
              ...x.parameters,
              year
            }
          }
        case 'TREND':
          return {
            ...x,
            parameters: {
              ...x.parameters,
              from: year - 1,
              to: year
            }
          }
      }
    })
  }
  return query
})

const { data, isLoading, fetch } = useFetchKpis(kpiQuery)

const config = computed<KPICardProps[]>(() => {
  return kpis.map((kpi, i) => {
    const kpiData = data.value?.kpis[i]
    const label = getLabel(kpi.kpi, kpiData ? kpiData.summary.value : null)
    return {
      title: getTitle(kpi, locale),
      kpi: label === '-' ? label : t(label),
      color: getColor(kpiData),
      showOnMap: showOnMapIndex.value === i,
      showOnMapEnabled: showOnMapEnabled.value,
      isLoading: isLoading.value,
      imgSrc: getIcon(kpiData),
      kpiDetails: {
        header: getHeader(locale),
        rows: getRows(kpiData?.summary, kpi, filters.submitted?.year, locale)
      },
      info: getKpiInfo(kpi, locale),
      fullscreen: sharedData?.fullscreen ?? false
    }
  })
})

const isMissingFilter = false
const kpiInfo = {
  sv: 'Mätvärdena ”Score”, ”Trend Attraktivitet” och ”Trend Skick” visar på årets beståndsanalys och trenden för måtten attraktivitet och skick i jämförelse med föregående år.',
  en: 'Measurement values for buildings in the complex'
}
// COLORED BUILDINGS

const showOnMapIndex = ref<number>()

onMounted(() => {
  fetch()
  showOnMapIndex.value = 0
})

const showOnMapData = computed<KpiResult | undefined>(() =>
  data.value?.kpis.at(showOnMapIndex.value ?? -1)
)

const coloredEntitiesData = computed<ColoredEntityData[] | undefined>(() => {
  const d = showOnMapData.value
  if (!d) return undefined
  const kpi = kpis[showOnMapIndex.value ?? 0]
  return d.values.map((v) => ({
    id: v.splitKey,
    meta: listToObject(getRows(v, kpi, filters.submitted?.year, locale)),
    value: v.value
  }))
})

const coloredEntities = useColoredEntities()

watch(
  coloredEntitiesData,
  () => {
    if (
      coloredEntitiesData.value !== undefined &&
      showOnMapData.value !== undefined &&
      showOnMapEnabled.value &&
      showOnMapIndex.value !== undefined
    ) {
      coloredEntities.set({
        key: showOnMapData.value.kpi,
        data: coloredEntitiesData.value,
        colorScale: colorScale[showOnMapData.value.kpi],
        title: getTitle(kpis[showOnMapIndex.value ?? 0], locale),
        unit: '',
        formatter: (v) => t(getLabel(showOnMapData.value!.kpi, v))
      })
    } else {
      coloredEntities.reset()
    }
  },
  { immediate: true }
)

onUnmounted(() => coloredEntities.reset())
</script>
<style lang="scss" scoped>
@use '@/design-system/design-tokens/font-mixins';
@use '@/design-system/design-tokens/variables.scss';

.kpi-section-header {
  display: flex;
  padding: 2px;
  margin-top: 16px;
  margin-bottom: 16px;
  align-content: flex-start;
  align-items: center;
}
.kpi-section-content {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 16px;
}

.title-row {
  @include font-mixins.desktop-bold-label;
  color: variables.$color-greyscale-content;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  gap: 4px;
}

.under-development {
  @include font-mixins.desktop-regular-tiny;
  color: red;
}

.legend {
  position: fixed;
  top: 64px;
  right: 0;
  margin: 16px;
}
</style>
